import { IData } from "@Components/shared/types";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssessmentDetails, IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { IColumnData } from "@Utils";
import { ChangeEvent } from "react";

const ADMIN_TABS = ["current", "new", "templates"] as const;
export const ASSESSMENT_STATUSES = ["assigned", "submitted", "completed"] as const;
export const SELECTION_TABS = ["users", "submissions", "edit"] as const;
const ASSESSMENT_TABS = [...ADMIN_TABS, ...SELECTION_TABS, ...ASSESSMENT_STATUSES] as const;
export type IAssessmentStatus = (typeof ASSESSMENT_STATUSES)[number];
export type IAssessmentTab = (typeof ASSESSMENT_TABS)[number];

export interface IAssessmentProps {
    assessmentId?: IAssessmentDetails["assessmentId"];
    tab?: IAssessmentTab;
}

export interface IAssessmentPageProps extends IAssessmentProps {
    hasSubmissions?: boolean;
}

export interface IAssessmentViewProps<T = unknown, U = T, V = U> {
    columnData?: IColumnData<T>[];
    data?: IData<T & U & V>;
    disabledFields?: Partial<Record<keyof U, { isDisabled: boolean; message?: string }>>;
    isDisabled?: boolean;
    isFetching?: boolean;
    isLoading?: boolean;
    isSubmitting?: boolean;
    tab?: IAssessmentTab;
    filterText?: string;

    onFilter?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export interface IUseAssessmentData<T = unknown, U = T, V = U> {
    columnData?: IColumnData<T>[];
    data?: IData<T & U & V>;
    disabledFields?: Partial<Record<keyof U, { isDisabled: boolean; message?: string }>>;
    isDisabled?: boolean;
    isFetched?: boolean;
    isFetching?: boolean;
    isLoading?: boolean;
    isSubmitting?: boolean;
    tab?: IAssessmentTab;
    filterText?: string;
    onExport?: () => Promise<boolean>;
    onReload?: () => void;
    onFilter?: (event: ChangeEvent<HTMLInputElement>) => void;
}

export interface IAssessmentView {
    tab?: IAssessmentTab;
}

export interface IExtendedTaskSubmissionDetails extends ITaskSubmissionDetails {
    activityLabel: IActivityDetails["activityLabel"];
    count: number;
    creationDate: string;
    quantity: number;
}

export interface IExtendedAssessmentInstanceDetails extends IAssessmentInstanceDetails {
    name: IAssessmentDetails["name"];
    onTime: boolean;
    owner?: IAssessmentDetails["owner"];
    tasks: IExtendedTaskSubmissionDetails[];
}

export interface IAssessmentMetadataRequest {
    assessmentId: string;
    assessmentInstanceId: string;
}

export const isValidTab = (str: string): str is IAssessmentTab => ASSESSMENT_TABS.includes(str as IAssessmentTab);
